<template>
    <div class="company-merge-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="header-title">
                    <merge-svg></merge-svg><h3>{{ currentStep.title }}</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="step" v-show="currentStep === steps.companies">
                    <!-- Begin Main Display -->
                    <div class="main-display" v-show="!selectedCompany">
                        <div class="intro" >
                            <p>If you have duplicate records, you can merge them here. This will help you consolidate company details, contacts, activities and opportunities all under one company.</p>
                            <p>Select the company you want all data merged into</p>
                        </div>
                        <div class="company-container" 
                            v-for="company in companies" 
                            :key="company.id"
                            :class="{ 'selected-company': company.selected }"
                            @click="selectCompany(company)">
                            <div class="logo-container">
                                <app-profile-image class="logo" :src="company.logoUrl || require('@/assets/images/company-avatar.png')" />
                            </div>
                            <div class="company-info-container">
                                <h4>{{company.name || 'No name'}}</h4>
                                <div class="company-content">
                                    <div class="company-honey-info">
                                        <p>Approximately {{company.approximateEmployeeCount}} employees</p>
                                        <p>{{company.contactsCount}} accounts in GlassHive</p>
                                    </div>
                                    <div class="company-contact-container">
                                        <p>{{company.website || 'No website listed'}}</p>
                                        <p>{{company.phone || 'No phone listed'}}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Begin Selected Display -->
                    <div class="selected-display" v-if="selectedCompany">
                        <div class="intro" >
                            <p>Company you want all data merged into:</p>
                        </div>
                        <!-- Selected Company -->
                        <div class="company-container the-selected-company">
                            <div class="close-container" @click="unselectCompany()">
                                <widget-close-svg />
                            </div>
                            <div class="logo-container">
                                <app-profile-image class="logo" :src="selectedCompany.logoUrl || require('@/assets/images/company-avatar.png')" />
                            </div>
                            <div class="company-info-container">
                                <h4>{{selectedCompany.name || 'No name'}}</h4>
                                <div class="company-content">
                                    <div class="company-honey-info">
                                        <p>Approximately {{selectedCompany.approximateEmployeeCount}} employees</p>
                                        <p>{{selectedCompany.contactsCount}} accounts in GlassHive</p>
                                    </div>
                                    <div class="company-contact-container">
                                        <p>{{selectedCompany.website || 'No website listed'}}</p>
                                        <p>{{selectedCompany.phone || 'No phone listed'}}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr />
                        <!-- Unselected Companies -->
                        <div class="intro" >
                            <p>Companies that will be merged and deleted:</p>
                        </div>
                        <div class="company-container" 
                            v-for="company in unselectedCompanies" 
                            :key="company.id"
                            @click="selectCompany(company)">
                            <div class="logo-container">
                                <app-profile-image class="logo" :src="company.logoUrl  || require('@/assets/images/company-avatar.png')" />
                            </div>
                            <div class="company-info-container">
                                <h4>{{company.name || 'No name'}}</h4>
                                <div class="company-content">
                                    <div class="company-honey-info">
                                        <p>Approximately {{company.approximateEmployeeCount}} employees</p>
                                        <p>{{company.contactsCount}} accounts in GlassHive</p>
                                    </div>
                                    <div class="company-contact-container">
                                        <p>{{company.website || 'No website listed'}}</p>
                                        <p>{{company.phone || 'No phone listed'}}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="step" v-show="currentStep === steps.data">
                    <div class="intro" v-if="selectedCompany">
                        <p>We need to know what your merged companies information should be.</p>
                        <p>You have chosen <strong>{{ selectedCompany.name || 'No name' }}</strong> to be the main company you want your data to be merged into. Information from <strong>{{ selectedCompany.name || 'No name' }}</strong> has been prefilled below. If the other companies that you selected also had information, you'll be able to select it from the drop-downs. Fields with no data were left blank for you to fill in.</p>
                    </div>
                    <div class="merge-select-container">
                        <div>
                            <label>Logo</label>
                            <div class="merged-logo-container app-multiselect-new-wrapper">
                                <app-profile-image
                                    v-if="mergedCompany.logoUrl" 
                                    :src="mergedCompany.logoUrl.value" />
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.logoUrl" 
                                    :options="logoOptions"
                                    label="name"
                                    placeholder="Select logo..." />
                            </div>
                        </div>
                        <div>
                            <div v-if="nameOptions.length < 2" class="has-label">
                                <app-input
                                    v-model="mergedCompany.name" 
                                    :label="'Company Name'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.name" 
                                    :options="nameOptions"
                                    placeholder="Select name..." />
                                <label>Name</label>
                            </div>
                        </div>
                        <div class="has-label">
                            <company-types-dropdown 
                                v-model="mergedCompany.type" 
                                :clientId="clientId" 
                                :allowManage="false" />
                            <label>Type</label>
                        </div>
                        <div class="has-label">
                            <vertical-dropdown
                                v-model="mergedCompany.vertical"
                                :clientId="clientId"
                                :allowManage="false"  />
                            <label>Vertical</label>
                        </div>
                        <div>
                            <div v-if="websiteOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.website" 
                                    :label="'Website'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.website" 
                                    :options="websiteOptions"
                                    placeholder="Select Website..." />
                                <label>Website</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="emailOptions.length < 2" class="has-label">
                                <app-input 
                                v-model="mergedCompany.email"
                                :label="'Email'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.email" 
                                    :options="emailOptions"
                                    placeholder="Select Email..." />
                                <label>Email</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="phoneOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.phone" 
                                    :label="'Phone'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.phone" 
                                    :options="phoneOptions"
                                    placeholder="Select Phone Number..." />
                                <label>Phone</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="addressOptions.length < 2" class="has-label">
                                 <app-input 
                                    v-model="mergedCompany.address" 
                                    :label="'Address'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.address" 
                                    :options="addressOptions"
                                    placeholder="Select Address..." />
                                <label>Address</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="address2Options.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.address2" 
                                    :label="'Address 2'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.address2" 
                                    :options="address2Options"
                                    placeholder="Select Address 2..." />
                                <label>Address 2</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="cityOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.city"
                                    :label="'City'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.city" 
                                    :options="cityOptions"
                                    placeholder="Select City..." />
                                <label>City</label>
                            </div>
                        </div>
                        <div>                            
                            <div v-if="regionOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.region" 
                                    :label="'State'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.region" 
                                    :options="regionOptions"
                                    placeholder="Select State..." />
                                <label>State</label>
                            </div>
                        </div>
                        <div>                            
                            <div v-if="zipCodeOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.zipCode" 
                                    :label="'ZIP Code'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.zipCode" 
                                    :options="zipCodeOptions"
                                    placeholder="Select ZIP Code..." />
                                <label>ZIP Code</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="countryOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.country" 
                                    :label="'Country'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.country" 
                                    :options="countryOptions"
                                    placeholder="Select Country..." />
                                <label>Country</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="linkedinUrlOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.linkedinUrl" 
                                    :label="'LinkedIn URL'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.linkedinUrl" 
                                    :options="linkedinUrlOptions"
                                    placeholder="Select LinkediI URL..." />
                                <label>LinkedIn URL</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="facebookUrlOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.facebookUrl" 
                                    :label="'Facebook URL'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.facebookUrl" 
                                    :options="facebookUrlOptions"
                                    placeholder="Select Facebook URL..." />
                                <label>Facebook URL</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="twitterUrlOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.twitterUrl" 
                                    :label="'X (Formerly Twitter) URL'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.twitterUrl" 
                                    :options="twitterUrlOptions"
                                    placeholder="Select X (Formerly Twitter) URL..." />
                                <label>X (Formerly Twitter) URL</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="youtubeUrlOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.youtubeUrl" 
                                    :label="'YouTube URL'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.youtubeUrl" 
                                    :options="youtubeUrlOptions"
                                    placeholder="Select YouTube URL..." />
                                <label>YouTube URL</label>
                            </div>
                        </div>
                        <div>
                            <div v-if="employeeCountOptions.length < 2" class="has-label">
                                <app-input 
                                    v-model="mergedCompany.employeeCount" 
                                    :label="'Employee Count'" />
                            </div>
                            <div v-else class="has-label app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.employeeCount" 
                                    :options="employeeCountOptions"
                                    placeholder="Select Employee Count..." />
                                <label>Employee Count</label>
                            </div>
                        </div>
                        <div>
                            <div class="description-container has-label" v-if="descriptionOptions.length < 2" >
                                <textarea type="text" class="form-control" v-model="mergedCompany.description"></textarea>
                                <label>Description</label>
                            </div>
                            <div v-else class="has-label">
                                <multiselect class="app-multiselect-new"
                                    v-model="mergedCompany.description" 
                                    :options="descriptionOptions"
                                    placeholder="Select Description..." />
                                <label>Description</label>
                            </div>
                        </div>
                    </div> <!-- End Merge Select Container -->
                </div> <!-- End Merge Select Step -->
                <div class="step" v-show="currentStep === steps.confirm">
                    <div class="boxed-container center-content" v-if="selectedCompany">
                        <div class="confirm-logo-container">
                            <div class="svg-container">
                                <img src="@/assets/images/SVG/merge-companies.svg" alt="merge companies">
                            </div>
                            <span class="fa fa-arrow-right"></span>
                            <div >
                                <app-profile-image class="logo" :src="selectedCompany.logoUrl  || require('@/assets/images/company-avatar.png')" />
                            </div>
                        </div>
                        <div class="companies-to-be-merged">
                            {{ companyNames }}
                        </div> 
                        <strong>will be merged into</strong>
                        <div class="companies-being-merged">
                            <p>{{ selectedCompany.name || 'No Name'}}</p>
                        </div> 
                    </div>
                    <div class="boxed-container">
                        <span class="boxed-container-header"><strong>Are you sure you want to merge these companies profiles</strong></span>
                        <p>Clicking <strong>'Merge'</strong> will merge company details, contacts, activities and opportunities into the main company profile selected, and will delete the other company profiles.</p>
                        <div class="merge-warning">
                            <warning-red-svg /> This action can't be undone <warning-red-svg />
                        </div>
                    </div>
                </div>
                <div class="step" v-show="currentStep === steps.merging || currentStep === steps.loading">
                    <loading-screen-svg />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="goToPreviousStep" v-show="currentStep !== steps.companies" class="transparent-btn">Back</app-button>
                    <app-button @click="currentStep.action" :loading="currentStep.loading">
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const CompanyTypesDropdown = () => import('@/components/company/company-types-dropdown');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const MergeSvg = () => import('@/components/svg/merge-svg');
const WarningRedSvg = () => import('@/components/svg/warning-red-svg');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const Multiselect = () => import('vue-multiselect');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg.vue');
const VerticalDropdown = () => import('@/components/vertical/vertical-dropdown');

import DashboardArrow from '@/components/common/app-dashboard-arrow';
import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'company-merge-widget',

    props: {
        options: {
            type: Object
        },

        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'widget-close-svg': WidgetCloseSvg,
        'merge-svg': MergeSvg,
        'app-profile-image': AppProfileImage,
        'warning-red-svg': WarningRedSvg,
        'dashboard-arrow': DashboardArrow,
        'multiselect': Multiselect,
        'company-types-dropdown': CompanyTypesDropdown,
        'loading-screen-svg': LoadingScreenSvg,
        'vertical-dropdown': VerticalDropdown
    },

    data() {
        return {
            clientId: null,
            companies: [],

            currentStep: null,

            mergedCompany: {
                logoUrl: null,
                name: null,
                phone: null,
                website: null,
                email: null,
                address: null,
                address2: null,
                city: null,
                region: null,
                zipcode: null,
                country: null,
                linkedinUrl: null,
                facebookUrl: null,
                twitterUrl: null,
                youtubeUrl: null,
                employeeCount: null,
                description: null,
                type: null,
                vertical: null
            },

            steps: {
                loading: { title: 'Loading...', buttonText: 'Loading...', loading: true, action: () => {} },
                companies: { title: 'Merge Companies', buttonText: 'Next Step', loading: false, action: this.goToData },
                data: { title: 'Merge Companies', buttonText: 'Next Step', loading: false, action: this.goToConfirm },
                confirm: { title: 'Merge Companies', buttonText: 'Merge', loading: false, action: this.merge },
                merging: { title: 'Merging...', buttonText: 'Merging...', loading: true, action: () => {} }
            }
        }
    },

    computed: {
        selectedCompany() {
            return this.companies.find(company => company.selected);
        },

        unselectedCompanies() {
            return this.companies.filter(company => !company.selected);
        },

        companyNames() {
            var names = this.unselectedCompanies.map(company => company.name);

            if (names.length === 1) {
                return names[0];
            }
            else {
                var lastCompanyName = names.pop();
                return names.join(', ') + ' and ' + lastCompanyName;
            }
        },

        logoOptions() {
            var logoUrls = this.companies.map(company => {
                return {
                    name: company.name || 'No name',
                    value: company.logoUrl || require('@/assets/images/company-avatar.png')
                };
            });

            logoUrls = logoUrls.filter(logoUrl => logoUrl.value); // not empty

            return logoUrls;
        },

        nameOptions() {
            var names = this.companies.map(company => company.name);
            names = names.filter(name => name); // not empty
            names = names.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return names;
        },

        phoneOptions() {
            var phones = this.companies.map(company => company.phone);
            phones = phones.filter(phone => phone); // not empty
            phones = phones.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return phones;
        },

        websiteOptions() {
            var websites = this.companies.map(company => company.website);
            websites = websites.filter(website => website); // not empty
            websites = websites.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return websites;
        },

        emailOptions() {
            var emails = this.companies.map(company => company.email);
            emails = emails.filter(email => email); // not empty
            emails = emails.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return emails;
        },

        addressOptions() {
            var addresses = this.companies.map(company => company.address);
            addresses = addresses.filter(address => address); // not empty
            addresses = addresses.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return addresses;
        },
        
        address2Options() {
            var addresses2 = this.companies.map(company => company.address2);
            addresses2 = addresses2.filter(address2 => address2); // not empty
            addresses2 = addresses2.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return addresses2;
        },

        cityOptions() {
            var cities = this.companies.map(company => company.city);
            cities = cities.filter(city => city); // not empty
            cities = cities.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return cities;
        },
        
        regionOptions() {
            var regions = this.companies.map(company => company.region);
            regions = regions.filter(region => region); // not empty
            regions = regions.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return regions;
        },
        
        zipCodeOptions() {
            var zipCodes = this.companies.map(company => company.zipCode);
            zipCodes = zipCodes.filter(zipCode => zipCode); // not empty
            zipCodes = zipCodes.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return zipCodes;
        },
        
        countryOptions() {
            var countries = this.companies.map(company => company.country);
            countries = countries.filter(country => country); // not empty
            countries = countries.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return countries;
        },
        
        linkedinUrlOptions() {
            var linkedinUrls = this.companies.map(company => company.linkedinUrl);
            linkedinUrls = linkedinUrls.filter(linkedinUrl => linkedinUrl); // not empty
            linkedinUrls = linkedinUrls.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return linkedinUrls;
        },
        
        facebookUrlOptions() {
            var facebookUrls = this.companies.map(company => company.facebookUrl);
            facebookUrls = facebookUrls.filter(facebookUrl => facebookUrl); // not empty
            facebookUrls = facebookUrls.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return facebookUrls;
        },
        
        twitterUrlOptions() {
            var twitterUrls = this.companies.map(company => company.twitterUrl);
            twitterUrls = twitterUrls.filter(twitterUrl => twitterUrl); // not empty
            twitterUrls = twitterUrls.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return twitterUrls;
        },
        
        youtubeUrlOptions() {
            var youtubeUrls = this.companies.map(company => company.youtubeUrl);
            youtubeUrls = youtubeUrls.filter(youtubeUrl => youtubeUrl); // not empty
            youtubeUrls = youtubeUrls.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return youtubeUrls;
        },
        
        employeeCountOptions() {
            var employeeCounts = this.companies.map(company => company.employeeCount);
            employeeCounts = employeeCounts.filter(employeeCount => employeeCount); // not empty
            employeeCounts = employeeCounts.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return employeeCounts;
        },

        descriptionOptions() {
            var descriptions = this.companies.map(company => company.description);
            descriptions = descriptions.filter(description => description); // not empty
            descriptions = descriptions.filter((value, index, self) => self.indexOf(value) === index); // distinct

            return descriptions;
        }

    },

    methods: {
        getData() {
            var filters = this.widget.options.companyIds.map(id => `Id eq ${ id }`);
            var $filter = `$filter=${ filters.join(' or ') }`;
            var $expand = `$expand=CompanyType,Vertical`;

            this.$api
                .get(`/service/Companies?${ $filter }&${ $expand }`)
                .then(response => {
                    this.companies = response.data.value.map(companyResponse => {
                        return {
                            id: companyResponse.Id,
                            name: companyResponse.Name,
                            logoUrl: companyResponse.LogoUrl,
                            employeeCount: companyResponse.ApproximateEmployeeCount || 0,
                            contactsCount: companyResponse.ContactsCount,
                            phone: companyResponse.Phone,
                            email: companyResponse.Email,
                            address: companyResponse.Address,
                            address2: companyResponse.Address2,
                            city: companyResponse.City,
                            region: companyResponse.Region,
                            zipCode: companyResponse.ZipCode,
                            country: companyResponse.Country,
                            linkedinUrl: companyResponse.LinkedinUrl,
                            facebookUrl: companyResponse.FacebookUrl,
                            twitterUrl: companyResponse.TwitterUrl,
                            youtubeUrl: companyResponse.YoutubeUrl,
                            website: companyResponse.Website,
                            description: companyResponse.Description,
                            selected: false,
                            type: companyResponse.CompanyType ? {
                                id: companyResponse.CompanyType.Id,
                                name: companyResponse.CompanyType.Name
                            } : null,
                            vertical: companyResponse.Vertical ? {
                                id: companyResponse.Vertical.Id,
                                name: companyResponse.Vertical.Name
                            } : null
                        };
                    });

                    this.goToStep('companies');
                });
        },

        goToData() {
            this.goToStep('data');
        },

        goToConfirm() {
            this.goToStep('confirm');
        },

        goToPreviousStep() {
            switch(this.currentStep) {
                case this.steps.data:
                    this.goToStep('companies');
                    break;
                case this.steps.confirm:
                    this.goToStep('data');
                    break;
            }
        },

        goToStep(step) {
            if (step === 'data') {
                this.prefillMergeFields();
            }

            this.currentStep = this.steps[step];
        },

        prefillMergeFields() {
            if (!this.mergedCompany.name) {
                if (this.selectedCompany.name) {
                    this.mergedCompany.name = this.selectedCompany.name;
                }
                else {
                    var companyWithName = this.companies.find(company => company.name);
                    if (companyWithName) {
                        this.mergedCompany.name = companyWithName.name;
                    }
                }
            }

            if (!this.mergedCompany.phone) {
                if (this.selectedCompany.phone) {
                    this.mergedCompany.phone = this.selectedCompany.phone;
                }
                else {
                    var companyWithPhone = this.companies.find(company => company.phone);
                    if (companyWithPhone) {
                        this.mergedCompany.phone = companyWithPhone.phone;
                    }
                }
            }

            if (!this.mergedCompany.website) {
                if (this.selectedCompany.website) {
                    this.mergedCompany.website = this.selectedCompany.website;
                }
                else {
                    var companyWithWebsite = this.companies.find(company => company.website);
                    if (companyWithWebsite) {
                        this.mergedCompany.website = companyWithWebsite.website;
                    }
                }
            }

            if (!this.mergedCompany.email) {
                if (this.selectedCompany.email) {
                    this.mergedCompany.email = this.selectedCompany.email;
                }
                else {
                    var companyWithEmail = this.companies.find(company => company.email);
                    if (companyWithEmail) {
                        this.mergedCompany.email = companyWithEmail.email;
                    }
                }
            }

            if (!this.mergedCompany.address) {
                if (this.selectedCompany.address) {
                    this.mergedCompany.address = this.selectedCompany.address;
                }
                else {
                    var companyWithAddress = this.companies.find(company => company.address);
                    if (companyWithAddress) {
                        this.mergedCompany.address = companyWithAddress.address;
                    }
                }
            }
            
            if (!this.mergedCompany.address2) {
                if (this.selectedCompany.address2) {
                    this.mergedCompany.address2 = this.selectedCompany.address2;
                }
                else {
                    var companyWithAddress2 = this.companies.find(company => company.address2);
                    if (companyWithAddress2) {
                        this.mergedCompany.address2 = companyWithAddress2.address2;
                    }
                }
            }

            if (!this.mergedCompany.city) {
                if (this.selectedCompany.city) {
                    this.mergedCompany.city = this.selectedCompany.city;
                }
                else {
                    var companyWithCity = this.companies.find(company => company.city);
                    if (companyWithCity) {
                        this.mergedCompany.city = companyWithCity.city;
                    }
                }
            }
            
            if (!this.mergedCompany.region) {
                if (this.selectedCompany.region) {
                    this.mergedCompany.region = this.selectedCompany.region;
                }
                else {
                    var companyWithRegion = this.companies.find(company => company.region);
                    if (companyWithRegion) {
                        this.mergedCompany.region = companyWithRegion.region;
                    }
                }
            }

            if (!this.mergedCompany.zipCode) {
                if (this.selectedCompany.zipCode) {
                    this.mergedCompany.zipCode = this.selectedCompany.zipCode;
                }
                else {
                    var companyWithZipcode = this.companies.find(company => company.zipCode);
                    if (companyWithZipcode) {
                        this.mergedCompany.zipCode = companyWithZipcode.zipCode;
                    }
                }
            }
            
            if (!this.mergedCompany.country) {
                if (this.selectedCompany.country) {
                    this.mergedCompany.country = this.selectedCompany.country;
                }
                else {
                    var companyWithCountry = this.companies.find(company => company.country);
                    if (companyWithCountry) {
                        this.mergedCompany.country = companyWithCountry.country;
                    }
                }
            }

            if (!this.mergedCompany.linkedinUrl) {
                if (this.selectedCompany.linkedinUrl) {
                    this.mergedCompany.linkedinUrl = this.selectedCompany.linkedinUrl;
                }
                else {
                    var companyWithLinkedin = this.companies.find(company => company.linkedinUrl);
                    if (companyWithLinkedin) {
                        this.mergedCompany.linkedinUrl = companyWithLinkedin.linkedinUrl;
                    }
                }
            }
            
            if (!this.mergedCompany.facebookUrl) {
                if (this.selectedCompany.facebookUrl) {
                    this.mergedCompany.facebookUrl = this.selectedCompany.facebookUrl;
                }
                else {
                    var companyWithFacebookUrl = this.companies.find(company => company.facebookUrl);
                    if (companyWithFacebookUrl) {
                        this.mergedCompany.facebookUrl = companyWithFacebookUrl.facebookUrl;
                    }
                }
            }
            
            if (!this.mergedCompany.twitterUrl) {
                if (this.selectedCompany.twitterUrl) {
                    this.mergedCompany.twitterUrl = this.selectedCompany.twitterUrl;
                }
                else {
                    var companyWithTwitterURL = this.companies.find(company => company.twitterUrl);
                    if (companyWithTwitterURL) {
                        this.mergedCompany.twitterUrl = companyWithTwitterURL.twitterUrl;
                    }
                }
            }
            
            if (!this.mergedCompany.youtubeUrl) {
                if (this.selectedCompany.youtubeUrl) {
                    this.mergedCompany.youtubeUrl = this.selectedCompany.youtubeUrl;
                }
                else {
                    var companyWithYoutubeUrl = this.companies.find(company => company.youtubeUrl);
                    if (companyWithYoutubeUrl) {
                        this.mergedCompany.youtubeUrl = companyWithYoutubeUrl.youtubeUrl;
                    }
                }
            }
            
            if (!this.mergedCompany.employeeCount) {
                if (this.selectedCompany.employeeCount) {
                    this.mergedCompany.employeeCount = this.selectedCompany.employeeCount;
                }
                else {
                    var companyWithEmployeeCount = this.companies.find(company => company.employeeCount);
                    if (companyWithEmployeeCount) {
                        this.mergedCompany.employeeCount = companyWithEmployeeCount.employeeCount;
                    }
                }
            }
            
            if (!this.mergedCompany.description) {
                if (this.selectedCompany.description) {
                    this.mergedCompany.description = this.selectedCompany.description;
                }
                else {
                    var companyWithDescription = this.companies.find(company => company.description);
                    if (companyWithDescription) {
                        this.mergedCompany.description = companyWithDescription.description;
                    }
                }
            }

            if (!this.mergedCompany.type) {
                if (this.selectedCompany.type) {
                    this.mergedCompany.type = this.selectedCompany.type;
                }
                else {
                    var companyWithType = this.companies.find(company => company.type);
                    if (companyWithType) {
                        this.mergedCompany.type = companyWithType.type;
                    }
                }
            }

            
            if (!this.mergedCompany.vertical) {
                if (this.selectedCompany.vertical) {
                    this.mergedCompany.vertical = this.selectedCompany.vertical;
                }
                else {
                    var companyWithVertical = this.companies.find(company => company.vertical);
                    if (companyWithVertical) {
                        this.mergedCompany.vertical = companyWithVertical.vertical;
                    }
                }
            }

            if (!this.mergedCompany.logoUrl) {
                if (this.selectedCompany.logoUrl) {
                    this.mergedCompany.logoUrl = {
                        name: this.selectedCompany.name || 'No name',
                        value: this.selectedCompany.logoUrl || require('@/assets/images/company-avatar.png')
                    };
                }
                else {
                    var companyWithLogoUrl = this.companies.find(company => company.logoUrl);
                    if (companyWithLogoUrl) {
                        this.mergedCompany.logoUrl = {
                            name: companyWithLogoUrl.name || 'No name',
                            value: companyWithLogoUrl.logoUrl || require('@/assets/images/company-avatar.png')
                        };
                    }
                }
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        selectCompany(company) {
            if (this.selectedCompany) {
                this.selectedCompany.selected = false;
            }

            company.selected = true;
        },

        unselectCompany() {
            this.selectedCompany.selected = false;
        },
        
        merge() {
            this.goToStep('merging');

            this.patchCompany()
                .then(this.mergeCompanies)
                .then(() => {
                    EventBus.$emit('company:update');
                    this.$helpers.showSuccessNotification('Your companies have been merged.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while merging your companies.', { error });
                });
        },

        patchCompany() {
            var changes = [
                { 'name': 'LogoUrl', 'value': this.mergedCompany.logoUrl ? this.mergedCompany.logoUrl.value : null }, 
                { 'name': 'Name', 'value': this.mergedCompany.name }, 
                { 'name': 'Email', 'value': this.mergedCompany.email }, 
                { 'name': 'Phone', 'value': this.mergedCompany.phone }, 
                { 'name': 'Address', 'value': this.mergedCompany.address }, 
                { 'name': 'Address2', 'value': this.mergedCompany.address2 }, 
                { 'name': 'ZipCode', 'value': this.mergedCompany.zipCode }, 
                { 'name': 'Region', 'value': this.mergedCompany.region }, 
                { 'name': 'LinkedinUrl', 'value': this.mergedCompany.linkedinUrl }, 
                { 'name': 'FacebookUrl', 'value': this.mergedCompany.facebookUrl }, 
                { 'name': 'TwitterUrl', 'value': this.mergedCompany.twitterUrl }, 
                { 'name': 'YoutubeUrl', 'value': this.mergedCompany.youtubeUrl },
                { 'name': 'Description', 'value': this.mergedCompany.description },
                { 'name': 'Website', 'value': this.mergedCompany.website },
                { 'name': 'City', 'value': this.mergedCompany.city },
                { 'name': 'Country', 'value': this.mergedCompany.country },
                { 'name': 'ApproximateEmployeeCount', 'value': this.mergedCompany.employeeCount },
                { 'name': 'CompanyTypeId', 'value': this.mergedCompany.type ? this.mergedCompany.type.id : null },
                { 'name': 'VerticalId', 'value': this.mergedCompany.vertical ? this.mergedCompany.vertical.id : null }
            ];

            return this.$api.patch(`/api/Companies/${ this.selectedCompany.id }`, changes);
        },

        mergeCompanies() {
            var companyIds = this.unselectedCompanies.map(company => company.id);
            return this.$api.post(`/api/Companies/${ this.selectedCompany.id }/Merge`, companyIds);
        }
    },

    created() {
        this.clientId = this.widget.options.clientId;
        
        this.goToStep('loading');
        this.getData();
    }
}
</script>

<style>
.company-merge-widget .center-content {
    text-align: center;
}

.company-merge-widget .widget-header svg {
    width: 30px;
    display: inline-block;
    vertical-align: middle;
    fill: white;
    margin-right: 10px;
}

.company-merge-widget .widget-header h3 {
    display: inline-block;
    vertical-align: middle;
}

.company-merge-widget .company-container {
    border: 2px solid #DEDFE2;
    border-radius: 13px;
    margin-bottom: 10px;
    display: flex;
    padding: 15px 25px;
    position: relative;
    overflow: hidden;
}

.company-merge-widget .company-info-container {
    font-size: 14px;
}

.company-merge-widget .content-wrapper .intro {
    margin-bottom: 20px;
}

.company-merge-widget .company-container:hover {
    cursor: pointer;
    background: #F4FAFE;
    border-color: #18AEFF;
    color: #18AEFF;
}

.company-merge-widget .company-container.the-selected-company:hover {
    cursor: pointer;
    background: initial;
    border-color: #DEDFE2;
    color: inherit;
}

.company-merge-widget .company-container .logo-container {
    margin-right: 15px;
}

.company-merge-widget .company-container .company-content {
    display: flex;
}

.company-merge-widget .company-container .company-content > div {
    flex-grow: 1;
}

.company-merge-widget .company-container .company-content .company-honey-info {
    margin-right: 40px;
}

.company-merge-widget .company-container .company-content p {
    margin: 0;
    line-height: 21px;
}

.company-merge-widget .action-container {
    display: flex;
}

.company-merge-widget .action-container .btn {
    margin-left: 15px;
}

.company-merge-widget .action-container .transparent-btn {
    background: transparent;
    color: #18AEFF;
}

.company-merge-widget .action-container .transparent-btn:hover {
    background: #DBEDFA;
}

.company-merge-widget .boxed-container {
    border: 2px solid #E9E9EF;
    border-radius: 22px;
    padding: 40px;
    margin-bottom: 30px;
    position: relative;
}

.company-merge-widget .boxed-container .boxed-container-header {
    position: absolute;
    top: -11px;
    left: 0;
    text-align: center;
    width: 100%;
}

.company-merge-widget .boxed-container .boxed-container-header strong {
    padding: 0 20px;
    background: #fff;
}

.company-merge-widget .confirm-logo-container {
    display: flex;
    justify-content: space-around;
    width: 300px;
    margin: 0 auto 20px auto;
    align-items: center;
}

.company-merge-widget .confirm-logo-container .svg-container {
    max-width: 105px;
}

.company-merge-widget .confirm-logo-container .background-image {
    border-radius: 0;
    height: 125px;
    width: 125px;
}

.company-merge-widget .merge-warning {
    text-align: center;
    color: #EE206F;
}

.company-merge-widget .merge-warning svg {
    vertical-align: middle;
    width: 20px;
}

.company-merge-widget .close-container {
    position: absolute;
    right: 0;
    top: 0;
    width: 60px;
    height: 60px;
}

.company-merge-widget .close-container::before {
    content: '';
    height: 0px;
    width: 0px;
    display: inline-block;
    background: #F5D5E7;
    border-radius: 50%;
    top: -15px;
    right: -15px;
    position: absolute;
    -webkit-transition: all 0.2s ease-in-out;
    transition: all 0.2s ease-in-out;
}

.company-merge-widget .close-container:hover::before {
    height: 60px;
    width: 60px;
}

.company-merge-widget .close-container svg {
    height: 25px;
    right: -35px;
    position: relative;
    top: 2px;
}

.company-merge-widget .close-container svg .a {
    stroke:#EE206F;
}

.company-merge-widget .has-label {
    display: flex;
    flex-direction: column-reverse;
}

.company-merge-widget .merge-select-container > div {
    margin-bottom: 20px;
}

.company-merge-widget label {
    font-size: 12px;
    color: #877D88;
}

.company-merge-widget .app-input:focus-within + label,
.company-merge-widget textarea:focus + label  {
    color: #18AEFF;
}

.company-merge-widget .merged-logo-container {
    display: flex;
    align-items: center;
}

.company-merge-widget .merged-logo-container  .background-image {
    height: 110px;
    width: 110px;
    margin-right: 20px;
}

.company-merge-widget textarea {
    border: 1px solid #E9E9F0;
    width: 100%;
    max-width: 100%;
    min-height: 150px;
}

.company-merge-widget .step .loading-screen {
    text-align: center;
}

.company-merge-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877D88;
}

.company-merge-widget .cancel-container .btn.transparent-btn:hover {
    background: #E1E0E5;
}
</style>